import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Headphones, Menu, Phone, Plane, User, X } from 'lucide-react';
import { AFFILIATE_DISCLOSURE, MY_BOOKINGS_LABEL, PHONE, SITE_NAME, SITE_TAGLINE } from '../constants/site';

const NAV = [
  { to: '/flight', label: 'Home', end: true },
  { to: '/flight/about', label: 'About Us' },
  { to: '/flight/support', label: 'Contact Us' },
  { to: '/flight/privacy', label: 'Privacy Policy' },
  { to: '/flight/terms', label: 'Terms & Conditions' },
];

function isNavActive(pathname, { to, end }) {
  if (end) return pathname === '/flight';
  return pathname === to;
}

export default function Header({ compact = false }) {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navPinned, setNavPinned] = useState(compact);
  const topBarRef = useRef(null);
  const navWrapRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    setNavPinned(compact);
  }, [compact]);

  useEffect(() => {
    if (compact) return undefined;

    const topBar = topBarRef.current;
    if (!topBar) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setNavPinned(!entry.isIntersecting),
      { threshold: 0, rootMargin: '0px 0px 0px 0px' }
    );
    observer.observe(topBar);
    return () => observer.disconnect();
  }, [compact]);

  useEffect(() => {
    const root = document.documentElement;

    const syncHeights = () => {
      if (topBarRef.current) {
        root.style.setProperty('--top-bar-height', `${topBarRef.current.offsetHeight}px`);
      }
      if (navWrapRef.current) {
        root.style.setProperty('--main-nav-height', `${navWrapRef.current.offsetHeight}px`);
      }
    };

    syncHeights();
    const ro = new ResizeObserver(syncHeights);
    if (topBarRef.current) ro.observe(topBarRef.current);
    if (navWrapRef.current) ro.observe(navWrapRef.current);
    window.addEventListener('resize', syncHeights);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncHeights);
    };
  }, [compact]);

  return (
    <header className={`site-header ${compact ? 'site-header--compact' : ''}`}>
      {!compact && (
        <div className="top-bar" ref={topBarRef}>
          <div className="container top-bar__inner">
            <p className="top-bar__disclosure">{AFFILIATE_DISCLOSURE}</p>
            <div className="top-bar__actions top-bar__actions--desktop">
              <a
                href={`tel:${PHONE.replace(/[^\d+]/g, '')}`}
                className="top-bar__phone"
                aria-label={`Call us 24/7 at ${PHONE}`}
              >
                <Phone size={15} aria-hidden />
                <span>
                  <small>Call Us 24/7</small>
                  <strong>{PHONE}</strong>
                </span>
              </a>
              <Link to="/flight/support" className="top-link">
                <Headphones size={15} aria-hidden />
                Contact
              </Link>
              <Link to="/flight/bookings" className="top-link top-link--accent">
                <User size={15} aria-hidden />
                {MY_BOOKINGS_LABEL}
              </Link>
            </div>
          </div>
        </div>
      )}
      <div className="main-nav-spacer" aria-hidden="true" />
      <div
        ref={navWrapRef}
        className={`main-nav-wrap${navPinned || compact ? ' is-pinned' : ''}${menuOpen ? ' is-menu-open' : ''}`}
      >
        <div className="container main-nav">
          <Link to="/flight" className="logo" onClick={() => setMenuOpen(false)}>
            <span className="logo__mark">
              <Plane size={22} aria-hidden />
            </span>
            <span className="logo__text">
              <strong>{SITE_NAME}</strong>
              {!compact && <small>{SITE_TAGLINE}</small>}
            </span>
          </Link>

          <button
            type="button"
            className="main-nav__toggle"
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
            aria-controls="main-navigation"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <nav
            id="main-navigation"
            className={`main-nav__links ${menuOpen ? 'is-open' : ''}`}
            aria-label="Main"
          >
            <div className="main-nav__drawer-head">
              <span className="main-nav__drawer-title">Menu</span>
              <button
                type="button"
                className="main-nav__drawer-close"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
              >
                <X size={22} aria-hidden />
              </button>
            </div>
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={isNavActive(pathname, item) ? 'active' : ''}
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/flight/bookings"
              className={`btn-gold main-nav__bookings${pathname === '/flight/bookings' ? ' main-nav__bookings--active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              <User size={16} aria-hidden />
              {MY_BOOKINGS_LABEL}
            </Link>
            <div className="main-nav__mobile-extras">
              <p className="main-nav__mobile-extras-label">Contact &amp; support</p>
              <a href={`tel:${PHONE.replace(/[^\d+]/g, '')}`}>
                <Phone size={16} aria-hidden />
                Call {PHONE}
              </a>
              <Link to="/flight/support" onClick={() => setMenuOpen(false)}>
                <Headphones size={16} aria-hidden />
                Contact Us
              </Link>
            </div>
          </nav>
        </div>
        {menuOpen && (
          <button
            type="button"
            className="main-nav__backdrop"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          />
        )}
      </div>
    </header>
  );
}
